/**
 * Centred and minimal layouts — the quiet end of the range, for apps whose screenshots should
 * read as calm rather than loud.
 *
 * These are also the layouts that break first when a translation is long, because centred text
 * has nowhere to wrap to. Every caption here relies on `fit: 'shrink'` and the pure-geometry
 * check across all four families.
 */
import { Template } from '@keymaker/schema';
import { bandBelow, fittedDevice, gradient, solid, text } from '../kit';

const CENTERED = { x: 0.08, y: 0.04, width: 0.84, height: 0.16 };

export const centeredCaption = Template.parse({
  id: 'centered-caption',
  name: 'Centred headline',
  category: 'centered',
  description: 'A centred headline with the device centred beneath it.',
  tags: ['minimal', 'centered'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: gradient(['#1D4ED8', '#111C4E']),
    layers: [
      text({ content: '{{caption.1}}', box: CENTERED, size: 0.040, align: 'center' }),
      fittedDevice({ top: bandBelow(CENTERED), align: 'center', side: 0.06 }),
    ],
  },
});

export const minimalLight = Template.parse({
  id: 'minimal-light',
  name: 'Minimal light',
  category: 'centered',
  description: 'A flat light canvas with dark type, so the device carries the colour.',
  tags: ['minimal', 'light', 'clean'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: solid('#F4F5F8'),
    layers: [
      text({
        content: '{{caption.1}}',
        box: { x: 0.06, y: 0.045, width: 0.88, height: 0.12 },
        size: 0.040,
        color: '#0D1220',
      }),
      fittedDevice({
        top: bandBelow({ y: 0.045, height: 0.12 }),
        side: 0.04,
        shadow: { blur: 0.055, opacity: 0.28, offsetY: 0.014, color: '#0D1220' },
      }),
    ],
  },
});

export const numberLed = Template.parse({
  id: 'number-led',
  name: 'Number led',
  category: 'centered',
  description: 'A large number carries the panel, with the caption beneath it.',
  tags: ['metric', 'proof', 'bold'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: gradient(['#7A2E1B', '#2B1109']),
    layers: [
      text({
        id: 'metric',
        content: '{{values.metric}}',
        box: { x: 0.06, y: 0.045, width: 0.88, height: 0.19 },
        size: 0.16,
        align: 'center',
        weight: 800,
        letterSpacing: -0.04,
      }),
      text({
        id: 'caption',
        content: '{{caption.1}}',
        box: { x: 0.06, y: 0.235, width: 0.88, height: 0.08 },
        size: 0.030,
        align: 'center',
        weight: 500,
        color: '#FFE3D6',
      }),
      fittedDevice({ top: bandBelow({ y: 0.235, height: 0.08 }), align: 'center', side: 0.09 }),
    ],
  },
});

export const quoteLed = Template.parse({
  id: 'quote-led',
  name: 'Quote led',
  category: 'centered',
  description: 'A large centred statement, with the device anchored at the foot of the panel.',
  tags: ['testimonial', 'editorial'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: solid('#12141C'),
    layers: [
      text({
        content: '{{caption.1}}',
        box: { x: 0.09, y: 0.18, width: 0.82, height: 0.26 },
        size: 0.048,
        align: 'center',
        valign: 'middle',
        lineHeight: 1.16,
      }),
      // The band has to be given real height. A short one caps the device hard: at 0.48 of the
      // canvas the phone is 675px wide, which is the largest it can be while the quote still
      // dominates — the first attempt at 0.34 rendered it a 478px stamp, unreadable.
      fittedDevice({ top: 0.52, bottom: 1, side: 0.01, align: 'bottom' }),
    ],
  },
});

export const centeredTemplates = [centeredCaption, minimalLight, numberLed, quoteLed];
